import { useQuery } from '@tanstack/react-query'
import { analyticsService } from '@/services/analyticsService'

export const useAnalytics = () => {
  // Get content analytics
  const useContentAnalytics = (contentId: string, timeRange: string = '7d') => {
    return useQuery({
      queryKey: ['content-analytics', contentId, timeRange],
      queryFn: () => analyticsService.getContentAnalytics(contentId, timeRange),
      enabled: !!contentId,
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1
    })
  }

  // Get tenant analytics
  const useTenantAnalytics = (tenantId: string, timeRange: string = '7d') => {
    // In development, check if user is authenticated via localStorage
    const isAuthenticated = import.meta.env.DEV 
      ? !!localStorage.getItem('cosmk_dev_user')
      : true // Assume authenticated in production (handled by auth context)

    return useQuery({
      queryKey: ['tenant-analytics', tenantId, timeRange],
      queryFn: () => analyticsService.getTenantAnalytics(tenantId, timeRange),
      enabled: !!tenantId && isAuthenticated,
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1
    })
  }

  // Get platform analytics (admin only)
  const usePlatformAnalytics = (timeRange: string = '7d') => {
    return useQuery({
      queryKey: ['platform-analytics', timeRange],
      queryFn: () => analyticsService.getPlatformAnalytics(timeRange),
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1
    })
  }

  return {
    useContentAnalytics,
    useTenantAnalytics,
    usePlatformAnalytics
  }
}

export default useAnalytics 